'use client';

import {
  CredentialResponse,
  GoogleLogin,
  GoogleOAuthProvider,
} from '@react-oauth/google';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import {
  Building2,
  CheckCircle2,
  Loader2,
  LockKeyhole,
  UserRound,
} from 'lucide-react';

import { useToast } from '@/components/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiClient } from '@/lib/auth/apiClient';
import { google } from '@/lib/auth/actions';

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '';

type ProviderType = 'private' | 'company';

type GoogleProfile = {
  email: string;
  name: string;
  surname: string;
  picture?: string | null;
};

type GoogleAuthResponse = {
  success: boolean;
  isNewUser: boolean;
  message?: string;
  error?: string;
  googleProfile?: GoogleProfile;
  user?: Record<string, any>;
  accessToken?: string;
  refreshToken?: string;
  deviceId?: string;
  token?: string;
};

type GoogleCompleteForm = {
  providerType: ProviderType;
  phone: string;
  city: string;

  companyName: string;
  companyOib: string;
  companyAddress: string;
  companyCity: string;
  companyPostalCode: string;
  companyCountry: string;

  isCompanyInVat: boolean;
  taxId: string;
  terms: boolean;
};

const INITIAL_FORM: GoogleCompleteForm = {
  providerType: 'private',
  phone: '',
  city: '',

  companyName: '',
  companyOib: '',
  companyAddress: '',
  companyCity: '',
  companyPostalCode: '',
  companyCountry: 'HR',

  isCompanyInVat: false,
  taxId: '',
  terms: false,
};
const API_URL =
  process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api/v1';
type GoogleAuthButtonProps = {
  signupFlow?: 'ticketing';
};

export default function GoogleAuthButton({
  signupFlow,
}: GoogleAuthButtonProps) {
  const router = useRouter();
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [credential, setCredential] = useState('');
  const [googleProfile, setGoogleProfile] = useState<GoogleProfile | null>(
    null,
  );

  const [form, setForm] = useState<GoogleCompleteForm>(INITIAL_FORM);

  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  const updateField = <K extends keyof GoogleCompleteForm>(
    field: K,
    value: GoogleCompleteForm[K],
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  /**
   * Prilagodi samo nazive ključeva ako AuthProvider
   * koristi drugačiji način spremanja tokena.
   */
  const persistAuthResult = async (data: GoogleAuthResponse) => {
    const sendRes = await google(data);
    console.log(data, 'presist');
    if (sendRes.error) return toast.error(sendRes.error);
    toast.info('Refreshing');
    window.location.reload();
    return toast.success('Login successly');
  };

  const resetDialog = () => {
    setCredential('');
    setGoogleProfile(null);
    setForm(INITIAL_FORM);
    setDialogOpen(false);
  };

  const handleExistingUserLogin = (data: GoogleAuthResponse) => {
    persistAuthResult(data);

    toast.success(
      data.message || 'Uspješno ste se prijavili putem Google računa.',
    );

    // window.location.replace('/dashboard');
  };

  const handleSuccess = async (credentialResponse: CredentialResponse) => {
    const googleCredential = credentialResponse.credential;

    if (!googleCredential) {
      toast.error('Google nije vratio podatke potrebne za prijavu.');
      return;
    }

    setIsGoogleLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: googleCredential,
        }),
      });

      const data = (await response.json()) as GoogleAuthResponse;

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Google prijava nije uspjela.');
      }

      if (!data.isNewUser) {
        handleExistingUserLogin(data);
        return;
      }

      if (!data.googleProfile) {
        throw new Error('Google profil nije moguće učitati.');
      }

      setCredential(googleCredential);
      setGoogleProfile(data.googleProfile);

      setForm((current) => ({
        ...current,
        providerType: 'private',
      }));

      setDialogOpen(true);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Google prijava nije uspjela.',
      );
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const validateForm = () => {
    if (!credential || !googleProfile) {
      return 'Google sesija nije valjana. Pokušajte ponovno.';
    }

    if (!form.phone.trim()) {
      return 'Broj mobitela je obavezan.';
    }

    if (!form.city.trim()) {
      return 'Grad je obavezan.';
    }

    if (!form.providerType) {
      return 'Odaberite jeste li privatna osoba ili tvrtka.';
    }

    if (form.providerType === 'company') {
      if (!form.companyName.trim()) {
        return 'Naziv tvrtke je obavezan.';
      }

      if (!form.companyOib.trim()) {
        return 'OIB tvrtke je obavezan.';
      }

      if (!form.companyAddress.trim()) {
        return 'Adresa tvrtke je obavezna.';
      }

      if (!form.companyCity.trim()) {
        return 'Grad tvrtke je obavezan.';
      }

      if (!form.companyPostalCode.trim()) {
        return 'Poštanski broj je obavezan.';
      }

      if (form.isCompanyInVat && !form.taxId.trim()) {
        return 'VAT ID je obavezan za tvrtke u sustavu PDV-a.';
      }
    }

    if (!form.terms) {
      return 'Morate prihvatiti uvjete korištenja.';
    }

    return null;
  };

  const handleCompleteRegistration = async () => {
    const validationError = validateForm();

    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsCompleting(true);

    try {
      const payload = {
        credential,

        // Provider dashboard: uloga je poznata.
        role: 'provider',

        signupFlow,

        name: googleProfile?.name,
        surname: googleProfile?.surname,

        providerType: form.providerType,
        phone: form.phone.trim(),
        city: form.city.trim(),
        country: 'HR',

        companyName:
          form.providerType === 'company' ? form.companyName.trim() : null,

        companyOib:
          form.providerType === 'company' ? form.companyOib.trim() : null,

        companyAddress:
          form.providerType === 'company' ? form.companyAddress.trim() : null,

        companyCity:
          form.providerType === 'company' ? form.companyCity.trim() : null,

        companyPostalCode:
          form.providerType === 'company'
            ? form.companyPostalCode.trim()
            : null,

        companyCountry:
          form.providerType === 'company' ? form.companyCountry : null,

        isCompanyInVat:
          form.providerType === 'company' ? form.isCompanyInVat : false,

        taxId:
          form.providerType === 'company' && form.isCompanyInVat
            ? form.taxId.trim()
            : null,

        acceptedTermsAt: new Date().toISOString(),
      };

      const response = await fetch(`${API_URL}/auth/google/complete`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = (await response.json()) as GoogleAuthResponse;

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || 'Dovršavanje Google registracije nije uspjelo.',
        );
      }
      console.log(data, 'regi');
      persistAuthResult(data);

      toast.success(data.message || 'Partner račun uspješno je kreiran.');

      resetDialog();
      // window.location.replace('/dashboard');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Registracija nije uspjela.',
      );
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <div className='relative flex w-full justify-center'>
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={() => toast.error('Google prijava nije uspjela.')}
            text='continue_with'
            width='320'
          />

          {isGoogleLoading && (
            <div className='absolute inset-0 flex items-center justify-center rounded-md bg-white/80 backdrop-blur-sm'>
              <Loader2 className='h-5 w-5 animate-spin text-meet-green' />
            </div>
          )}
        </div>
      </GoogleOAuthProvider>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (isCompleting) return;

          if (!open) {
            resetDialog();
            return;
          }

          setDialogOpen(true);
        }}>
        <DialogContent className='max-h-[92dvh] w-[calc(100%-24px)] max-w-3xl overflow-y-auto rounded-[28px] border-black/10 p-0 shadow-2xl sm:w-full'>
          {/* HEADER */}
          <div className='border-b border-black/10 bg-[#fafafa] px-5 py-6 sm:px-8'>
            <DialogHeader className='text-left'>
              <div className='mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-meet-green-light text-black'>
                <CheckCircle2 className='h-6 w-6' />
              </div>

              <DialogTitle className='text-2xl font-semibold tracking-tight text-black sm:text-3xl'>
                Dovršite partner račun
              </DialogTitle>

              <DialogDescription className='max-w-xl text-sm leading-6 text-black/55'>
                Google račun uspješno je potvrđen. Dopunite podatke koji su nam
                potrebni za kreiranje vašeg Meet Croatia partner računa.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className='space-y-8 px-5 py-6 sm:px-8 sm:py-8'>
            {/* GOOGLE PROFILE */}
            <section className='space-y-4'>
              <SectionHeading
                title='Google račun'
                description='Ovi podaci preuzeti su iz vašeg Google profila.'
              />

              <div className='grid gap-4 sm:grid-cols-2'>
                <Field>
                  <Label htmlFor='google-name'>Ime</Label>
                  <Input
                    id='google-name'
                    value={googleProfile?.name || ''}
                    disabled
                    className='h-12 rounded-xl border-black/10 bg-zinc-50 shadow-none'
                  />
                </Field>

                <Field>
                  <Label htmlFor='google-surname'>Prezime</Label>
                  <Input
                    id='google-surname'
                    value={googleProfile?.surname || ''}
                    disabled
                    className='h-12 rounded-xl border-black/10 bg-zinc-50 shadow-none'
                  />
                </Field>
              </div>

              <Field>
                <Label htmlFor='google-email'>Email adresa</Label>

                <div className='relative'>
                  <Input
                    id='google-email'
                    type='email'
                    value={googleProfile?.email || ''}
                    disabled
                    className='h-12 rounded-xl border-black/10 bg-zinc-50 pr-11 shadow-none'
                  />

                  <CheckCircle2 className='absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-meet-green' />
                </div>
              </Field>
            </section>

            <div className='h-px bg-black/10' />

            {/* CONTACT */}
            <section className='space-y-4'>
              <SectionHeading
                title='Kontakt podaci'
                description='Koristit ćemo ih za obavijesti, podršku i upravljanje partner računom.'
              />

              <div className='grid gap-4 sm:grid-cols-2'>
                <Field>
                  <Label htmlFor='google-phone'>Broj mobitela</Label>

                  <Input
                    id='google-phone'
                    type='tel'
                    autoComplete='tel'
                    placeholder='+385 91 234 5678'
                    value={form.phone}
                    onChange={(event) =>
                      updateField('phone', event.target.value)
                    }
                    className='h-12 rounded-xl border-black/10 shadow-none focus-visible:border-meet-green focus-visible:ring-0'
                  />
                </Field>

                <Field>
                  <Label htmlFor='google-city'>Grad</Label>

                  <Input
                    id='google-city'
                    autoComplete='address-level2'
                    placeholder='Split'
                    value={form.city}
                    onChange={(event) =>
                      updateField('city', event.target.value)
                    }
                    className='h-12 rounded-xl border-black/10 shadow-none focus-visible:border-meet-green focus-visible:ring-0'
                  />
                </Field>
              </div>
            </section>

            <div className='h-px bg-black/10' />

            {/* PROVIDER TYPE */}
            <section className='space-y-4'>
              <SectionHeading
                title='Tko oglašava usluge?'
                description='Odaberite oblik poslovanja partner računa.'
              />

              <div className='grid gap-3 sm:grid-cols-2'>
                <ProviderTypeCard
                  title='Privatna osoba'
                  description='Oglašavam vlastiti smještaj, plovilo ili uslugu.'
                  icon={<UserRound className='h-6 w-6' />}
                  selected={form.providerType === 'private'}
                  onClick={() => updateField('providerType', 'private')}
                />

                <ProviderTypeCard
                  title='Tvrtka'
                  description='Oglašavam kao tvrtka, obrt ili turistička agencija.'
                  icon={<Building2 className='h-6 w-6' />}
                  selected={form.providerType === 'company'}
                  onClick={() => updateField('providerType', 'company')}
                />
              </div>
            </section>

            {/* COMPANY */}
            {form.providerType === 'company' && (
              <>
                <div className='h-px bg-black/10' />

                <section className='space-y-5'>
                  <SectionHeading
                    title='Podaci o tvrtki'
                    description='Podaci služe za identifikaciju partnera, poslovnu dokumentaciju i izdavanje računa.'
                  />

                  <div className='grid gap-4 sm:grid-cols-2'>
                    <Field>
                      <Label htmlFor='company-name'>Naziv tvrtke</Label>

                      <Input
                        id='company-name'
                        autoComplete='organization'
                        placeholder='Naziv tvrtke d.o.o.'
                        value={form.companyName}
                        onChange={(event) =>
                          updateField('companyName', event.target.value)
                        }
                        className='h-12 rounded-xl border-black/10 shadow-none focus-visible:border-meet-green focus-visible:ring-0'
                      />
                    </Field>

                    <Field>
                      <Label htmlFor='company-oib'>OIB</Label>

                      <Input
                        id='company-oib'
                        inputMode='numeric'
                        placeholder='12345678901'
                        value={form.companyOib}
                        onChange={(event) =>
                          updateField('companyOib', event.target.value)
                        }
                        className='h-12 rounded-xl border-black/10 shadow-none focus-visible:border-meet-green focus-visible:ring-0'
                      />
                    </Field>
                  </div>

                  <Field>
                    <Label htmlFor='company-address'>Adresa</Label>

                    <Input
                      id='company-address'
                      autoComplete='street-address'
                      placeholder='Ulica i kućni broj'
                      value={form.companyAddress}
                      onChange={(event) =>
                        updateField('companyAddress', event.target.value)
                      }
                      className='h-12 rounded-xl border-black/10 shadow-none focus-visible:border-meet-green focus-visible:ring-0'
                    />
                  </Field>

                  <div className='grid gap-4 sm:grid-cols-3'>
                    <Field>
                      <Label htmlFor='company-city'>Grad</Label>

                      <Input
                        id='company-city'
                        autoComplete='address-level2'
                        placeholder='Split'
                        value={form.companyCity}
                        onChange={(event) =>
                          updateField('companyCity', event.target.value)
                        }
                        className='h-12 rounded-xl border-black/10 shadow-none focus-visible:border-meet-green focus-visible:ring-0'
                      />
                    </Field>

                    <Field>
                      <Label htmlFor='company-postal'>Poštanski broj</Label>

                      <Input
                        id='company-postal'
                        inputMode='numeric'
                        autoComplete='postal-code'
                        placeholder='21000'
                        value={form.companyPostalCode}
                        onChange={(event) =>
                          updateField('companyPostalCode', event.target.value)
                        }
                        className='h-12 rounded-xl border-black/10 shadow-none focus-visible:border-meet-green focus-visible:ring-0'
                      />
                    </Field>

                    <Field>
                      <Label htmlFor='company-country'>Država</Label>

                      <Input
                        id='company-country'
                        value={form.companyCountry}
                        disabled
                        className='h-12 rounded-xl border-black/10 bg-zinc-50 shadow-none'
                      />
                    </Field>
                  </div>

                  <div className='rounded-2xl border border-black/10 bg-[#fafafa] p-4 sm:p-5'>
                    <div className='flex items-center justify-between gap-5'>
                      <div>
                        <p className='font-medium text-black'>
                          Tvrtka je u sustavu PDV-a
                        </p>

                        <p className='mt-1 text-sm leading-5 text-black/50'>
                          Aktivirajte opciju i unesite VAT ID.
                        </p>
                      </div>

                      <Switch
                        checked={form.isCompanyInVat}
                        onCheckedChange={(checked) => {
                          updateField('isCompanyInVat', checked);

                          if (!checked) {
                            updateField('taxId', '');
                          }
                        }}
                      />
                    </div>

                    {form.isCompanyInVat && (
                      <div className='mt-5'>
                        <Label htmlFor='company-tax-id'>VAT ID</Label>

                        <Input
                          id='company-tax-id'
                          className='mt-2 h-12 rounded-xl border-black/10 bg-white shadow-none focus-visible:border-meet-green focus-visible:ring-0'
                          placeholder='HR12345678901'
                          value={form.taxId}
                          onChange={(event) =>
                            updateField('taxId', event.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}

            <div className='h-px bg-black/10' />

            {/* TERMS */}
            <label className='flex cursor-pointer items-start gap-3 rounded-2xl border border-black/10 bg-white p-4 transition hover:bg-zinc-50'>
              <input
                type='checkbox'
                checked={form.terms}
                onChange={(event) => updateField('terms', event.target.checked)}
                className='mt-1 h-4 w-4 shrink-0 accent-meet-green'
              />

              <span className='text-sm leading-6 text-black/65'>
                Prihvaćam{' '}
                <Link
                  href='/legal/terms-of-use'
                  target='_blank'
                  className='font-semibold text-meet-green hover:underline'>
                  uvjete korištenja
                </Link>{' '}
                i potvrđujem točnost unesenih podataka.
              </span>
            </label>

            {/* ACTION */}
            <div className='sticky bottom-0 -mx-5 border-t border-black/10 bg-white/95 px-5 pb-1 pt-4 backdrop-blur-xl sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:pt-0'>
              <Button
                type='button'
                disabled={isCompleting}
                onClick={handleCompleteRegistration}
                className='h-13 w-full rounded-xl bg-meet-green text-base font-semibold text-white hover:bg-black'>
                {isCompleting ? (
                  <>
                    <Loader2 className='mr-2 h-5 w-5 animate-spin' />
                    Kreiram partner račun...
                  </>
                ) : (
                  <>
                    <LockKeyhole className='mr-2 h-5 w-5' />
                    Kreiraj partner račun
                  </>
                )}
              </Button>

              <p className='mt-3 text-center text-xs leading-5 text-black/45'>
                Sigurna Google autentifikacija. Lozinka nije potrebna.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SectionHeading({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div>
      <h3 className='text-sm font-semibold uppercase tracking-[0.12em] text-black/60'>
        {title}
      </h3>

      <p className='mt-1 text-sm leading-5 text-black/45'>{description}</p>
    </div>
  );
}

function Field({ children }: { children: React.ReactNode }) {
  return <div className='space-y-2'>{children}</div>;
}

function ProviderTypeCard({
  title,
  description,
  icon,
  selected,
  onClick,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`
        relative flex min-h-32 w-full flex-col items-start
        rounded-2xl border p-5 text-left
        transition-all duration-200
        ${
          selected
            ? 'border-2 border-meet-green bg-meet-green-light/20 shadow-[0_12px_35px_rgba(13,67,63,0.12)]'
            : 'border-black/10 bg-white hover:-translate-y-0.5 hover:border-meet-green/40 hover:shadow-lg'
        }
      `}>
      {selected && (
        <CheckCircle2 className='absolute right-4 top-4 h-5 w-5 text-meet-green' />
      )}

      <div
        className={`
          flex h-11 w-11 items-center justify-center rounded-xl
          ${
            selected
              ? 'bg-meet-green text-meet-green-light'
              : 'bg-zinc-100 text-black'
          }
        `}>
        {icon}
      </div>

      <p className='mt-4 font-semibold text-black'>{title}</p>

      <p className='mt-1 pr-5 text-sm leading-5 text-black/50'>{description}</p>
    </button>
  );
}
